import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg, useApi } from "@/lib/api";
import { useAuth } from "@clerk/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const KonsepPanel = () => {
  const api = useApi();
  const { getToken } = useAuth();
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => api("get", "/admin/konsep", null, { params: { q } }).then(setData).catch(() => {}), [api, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/admin/konsep/upload`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      if (!r.ok) throw { response: { data: j } };
      toast.success(`${j.jumlah} konsep dimuat naik`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="space-y-4" data-testid="konsep-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="eyebrow">Katalog 732KQ</div>
        <span className="font-mono-data text-xs text-mute" data-testid="konsep-total">
          {data?.meta ? `${data.meta.jumlah} konsep · ${data.meta.fail}` : "Belum dimuat naik"}
        </span>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="konsep-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="konsep-upload-button"
          className="ml-auto h-10 px-4 rounded-full bg-ink text-bg text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Muat naik CSV / XLSX
        </button>
      </div>
      <p className="text-xs text-mute">Lajur: kod, nama konsep, arab, huraian, ayat rujukan. Muat naik baharu menggantikan katalog sedia ada.</p>
      <label className="relative block">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-mute" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari konsep…" data-testid="konsep-search-input"
          className="w-full h-10 pl-9 pr-3 rounded-xl bg-bg border border-line text-sm focus:outline-none focus:border-[var(--q)]" />
      </label>
      <div className="rounded-xl border border-line overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Kod</TableHead><TableHead>Nama</TableHead><TableHead className="text-right">Arab</TableHead><TableHead>Huraian</TableHead><TableHead>Ayat</TableHead></TableRow></TableHeader>
          <TableBody>
            {(data?.items || []).map((k, i) => (
              <TableRow key={i} data-testid={`konsep-row-${i}`}>
                <TableCell className="font-mono-data text-xs">{k.kod}</TableCell>
                <TableCell className="text-sm">{k.nama}</TableCell>
                <TableCell className="arabic-sm text-lg text-right" lang="ar">{k.ar}</TableCell>
                <TableCell className="text-xs text-mute max-w-xs">{k.huraian}</TableCell>
                <TableCell className="font-mono-data text-xs">{k.ayat_refs.map((r) => `${r.surah}:${r.from}${r.to !== r.from ? `-${r.to}` : ""}`).join(", ") || k.ayat_raw}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data && data.items.length === 0 && <p className="p-4 text-sm text-mute" data-testid="konsep-empty">Tiada konsep.</p>}
      </div>
      {data?.total > 50 && <p className="text-xs text-mute">Memaparkan 50 daripada {data.total}. Guna carian untuk menapis.</p>}
    </div>
  );
};
